<template>
  <div
    v-if="warehouse === null || warehouse === undefined"
    class="m-5 p-5 text-center bold-40"
  >
    {{ $t("manufacturingCollectList.noWarehouseSelected2") }}
  </div>
  <div
    v-else-if="selectedWarehouse?.type !== 2"
    class="m-5 p-5 text-center bold-40"
  >
    {{ $t("manufacturingCollectList.thisWarehouseNotAProductionType") }}
  </div>
  <div v-else>
    <div class="row">
      <div class="col-12">
        <div class="nature-white-box">
          <div v-if="generating" class="alert alert-info text-center py-2 mb-2">
            <div
              class="spinner-border spinner-border-sm me-2"
              role="status"
            ></div>
            {{ $t("manufacturingGrant.generating") }}
          </div>
          <div
            v-if="errorMessage"
            class="alert alert-danger text-center py-2 mb-2"
          >
            <span>{{ errorMessage }}</span>
            <button
              class="btn btn-sm btn-outline-danger ms-3"
              @click="loadData"
            >
              {{ $t("manufacturingGrant.retry") }}
            </button>
          </div>
          <!-- Fejléc: napnavigáció · nézetváltó · mód · generálás -->
          <div class="pp-header">
            <div class="pp-daynav">
              <button
                class="day-nav"
                :title="$t('manufacturingGrant.previousDay')"
                @click="shiftDay(-1)"
              >
                <BaseIcon name="left-arrow" />
              </button>
              <div class="pp-daytitle">
                <div class="bold-24">{{ dayTitle }}</div>
                <div class="regular-12 text-gray">{{ daySub }}</div>
              </div>
              <button
                class="day-nav"
                :title="$t('manufacturingGrant.nextDay')"
                @click="shiftDay(1)"
              >
                <BaseIcon name="right-arrow" />
              </button>
              <button
                class="btn btn-sm btn-outline-primary pp-today"
                @click="goToday"
              >
                {{ $t("manufacturingGrant.today") }}
              </button>
            </div>
            <div class="flex-fill"></div>
            <div class="pp-actions">
              <div class="d-flex" v-if="list.length > 0">
                <button
                  class="btn btn-sm btn-no-r"
                  :class="viewType === 1 ? 'btn-primary' : 'btn-secondary'"
                  @click="viewType = 1"
                >
                  {{ $t("manufacturingGrant.userView") }}
                </button>
                <button
                  class="btn btn-sm btn-no-r btn-no-l"
                  :class="viewType === 2 ? 'btn-primary' : 'btn-secondary'"
                  @click="viewType = 2"
                >
                  {{ $t("manufacturingGrant.machineView") }}
                </button>
                <button
                  class="btn btn-sm btn-no-l"
                  :class="viewType === 0 ? 'btn-primary' : 'btn-secondary'"
                  @click="viewType = 0"
                >
                  {{ $t("manufacturingGrant.productView") }}
                </button>
              </div>
              <div class="dropdown" v-if="opNames.length">
                <button
                  class="btn btn-sm btn-secondary pp-opfilter"
                  data-bs-toggle="dropdown"
                  data-bs-auto-close="outside"
                  aria-expanded="false"
                >
                  <BaseIcon name="filter" />
                  <span>{{ $t("manufacturingGrant.operationsFilter") }}</span>
                  <span v-if="hiddenOpCount" class="pp-opfilter-badge">{{
                    $t("manufacturingGrant.hiddenCount", { n: hiddenOpCount })
                  }}</span>
                </button>
                <div class="dropdown-menu dropdown-menu-end pp-opfilter-menu">
                  <label
                    v-for="n in opNames"
                    :key="n"
                    class="dropdown-item d-flex align-items-center gap-2"
                  >
                    <input
                      type="checkbox"
                      class="form-check-input m-0"
                      :checked="!isHiddenOp(n)"
                      @change="toggleOp(n)"
                    />
                    <span :class="{ 'text-muted': isHiddenOp(n) }">{{
                      n
                    }}</span>
                  </label>
                  <div class="dropdown-divider" v-if="hiddenOpCount"></div>
                  <button
                    v-if="hiddenOpCount"
                    class="dropdown-item semibold-12"
                    @click="showAllOps"
                  >
                    {{ $t("manufacturingGrant.showAll") }}
                  </button>
                </div>
              </div>
              <button
                class="btn btn-sm btn-outline-primary pp-generate"
                @click="showGenerate"
              >
                <BaseIcon name="play-circle" />
                <span>{{ $t("manufacturingGrant.generateSchedule") }}</span>
              </button>
            </div>
          </div>

          <!-- Hétsáv (napi nézet) -->
          <div class="week-strip">
            <button
              v-for="wd in weekDays"
              :key="wd.date"
              class="week-day"
              :class="{
                today: wd.isToday,
                selected: wd.isSelected,
                holiday: wd.isHoliday,
              }"
              :title="
                (wd.isHoliday ? $t('manufacturingGrant.holiday') + ' · ' : '') +
                (wd.onLeaveCount
                  ? $t('manufacturingGrant.onLeaveCount', {
                      n: wd.onLeaveCount,
                    })
                  : '')
              "
              @click="selectDay(wd.date)"
            >
              <div class="wd-top">
                <span class="wd-day">{{ wd.dayNum }}</span>
                <span class="wd-dow">{{ wd.dow }}</span>
                <span class="wd-ops">{{ wd.opsLabel }}</span>
              </div>
              <div class="wd-track">
                <div class="wd-fill" :style="{ width: wd.loadPct + '%' }"></div>
              </div>
            </button>
          </div>

          <!-- Statisztikák -->
          <div
            v-if="list.length > 0"
            class="stats-bar bg-light rounded pp-stats"
          >
            <div class="stats-item" v-for="st in dayStats" :key="st.label">
              <span class="stats-label">{{ st.label }}:</span>
              <span class="stats-value" :style="{ color: st.color }">{{
                st.value
              }}</span>
            </div>
          </div>
          <div
            v-else-if="stats.total_operations > 0"
            class="stats-bar bg-light rounded pp-stats"
          >
            <div class="stats-item">
              <span class="stats-label"
                >{{ $t("manufacturingGrant.totalItems") }}:</span
              >
              <span class="stats-value">{{ stats.total_items }}</span>
            </div>
            <div class="stats-item">
              <span class="stats-label"
                >{{ $t("manufacturingGrant.totalOperations") }}:</span
              >
              <span class="stats-value">{{ stats.total_operations }}</span>
            </div>
            <div class="stats-item">
              <span class="stats-label"
                >{{ $t("manufacturingGrant.totalWorkers") }}:</span
              >
              <span class="stats-value">{{ stats.total_workers }}</span>
            </div>
            <div class="stats-item" v-if="stats.date_start">
              <span class="stats-label"
                >{{ $t("manufacturingGrant.dateRange") }}:</span
              >
              <span class="stats-value"
                >{{ formatDate(stats.date_start) }} &ndash;
                {{ formatDate(stats.date_end) }}</span
              >
            </div>
          </div>

          <!-- Generate Schedule Options Modal -->
          <div
            ref="generateModal"
            class="modal fade"
            tabindex="-1"
            aria-hidden="true"
          >
            <div class="modal-dialog modal-lg modal-dialog-centered">
              <div class="modal-content">
                <div class="modal-header">
                  <h5 class="modal-title">
                    {{ $t("manufacturingGrant.generateSchedule") }}
                  </h5>
                  <button
                    type="button"
                    class="btn-close"
                    data-bs-dismiss="modal"
                  ></button>
                </div>
                <div class="modal-body">
                  <div class="mb-3 custom-input">
                    <label class="form-label bold-12 text-gray">{{
                      $t("manufacturingGrant.policy")
                    }}</label>
                    <div class="glass-radio-group">
                      <input
                        type="radio"
                        name="policy"
                        id="policyUIS"
                        value="UIS"
                        v-model="generateOptions.policy"
                      />
                      <label class="glass-radio" for="policyUIS">{{
                        $t("manufacturingGrant.policyUISLabel")
                      }}</label>
                      <input
                        type="radio"
                        name="policy"
                        id="policyNIS"
                        value="NIS"
                        v-model="generateOptions.policy"
                      />
                      <label class="glass-radio" for="policyNIS">{{
                        $t("manufacturingGrant.policyNISLabel")
                      }}</label>
                    </div>
                    <div class="mt-2 text-muted-8 small italic-text">
                      {{
                        generateOptions.policy === "UIS"
                          ? $t("manufacturingGrant.policyUISHelp")
                          : $t("manufacturingGrant.policyNISHelp")
                      }}
                    </div>
                  </div>
                  <div class="row mb-3 custom-input whitebg-input">
                    <div class="col-4">
                      <label class="form-label bold-12 text-gray">{{
                        $t("manufacturingGrant.shiftStart")
                      }}</label>
                      <TimePicker
                        v-model="generateOptions.shift_start_time"
                        :minutes-increment="5"
                      />
                      <div class="mt-1 text-muted-8 small italic-text">
                        {{ $t("manufacturingGrant.shiftStartHelp") }}
                      </div>
                    </div>
                    <div class="col-4">
                      <label class="form-label bold-12 text-gray"
                        >{{ $t("manufacturingGrant.shiftLength") }} ({{
                          $t("manufacturingGrant.hours")
                        }})</label
                      >
                      <div class="fix-end-input">
                        <input
                          type="number"
                          class="form-control"
                          min="1"
                          max="24"
                          v-model.number="generateOptions.shift_length_hours"
                        />
                        <span>{{ $t("manufacturingGrant.hours") }}</span>
                      </div>
                      <div class="mt-1 text-muted-8 small italic-text">
                        {{ $t("manufacturingGrant.shiftLengthHelp") }}
                      </div>
                    </div>
                    <div class="col-4">
                      <label class="form-label bold-12 text-gray"
                        >{{ $t("manufacturingGrant.breakLength") }} ({{
                          $t("manufacturingGrant.hours")
                        }})</label
                      >
                      <div class="fix-end-input">
                        <input
                          type="number"
                          class="form-control"
                          min="0"
                          max="23"
                          v-model.number="generateOptions.break_length_hours"
                        />
                        <span>{{ $t("manufacturingGrant.hours") }}</span>
                      </div>
                      <div
                        class="mt-1 text-muted-8 small italic-text"
                        :class="{ 'text-danger': shiftCycleWarning }"
                      >
                        {{
                          shiftCycleWarning
                            ? $t("manufacturingGrant.shiftCycleWarning", {
                                sum: shiftCycleSum,
                              })
                            : $t("manufacturingGrant.breakLengthHelp", {
                                next: nextShiftStart,
                              })
                        }}
                      </div>
                    </div>
                  </div>
                  <div class="mb-3 custom-input">
                    <label class="form-label bold-12 text-gray">{{
                      $t("manufacturingGrant.innerBreak")
                    }}</label>
                    <div class="row g-2">
                      <div class="col-4">
                        <input
                          type="number"
                          min="0"
                          class="form-control"
                          v-model.number="generateOptions.inner_break_duration"
                        />
                        <small class="text-muted"
                          >{{ $t("manufacturingGrant.breakDuration") }} ({{
                            $t("manufacturingGrant.minutes")
                          }})</small
                        >
                      </div>
                      <div class="col-4">
                        <input
                          type="time"
                          class="form-control"
                          v-model="generateOptions.inner_break_start_time"
                        />
                        <small class="text-muted">{{
                          $t("manufacturingGrant.breakWindowStart")
                        }}</small>
                      </div>
                      <div class="col-4">
                        <input
                          type="time"
                          class="form-control"
                          v-model="generateOptions.inner_break_end_time"
                        />
                        <small class="text-muted">{{
                          $t("manufacturingGrant.breakWindowEnd")
                        }}</small>
                      </div>
                    </div>
                  </div>
                  <div class="mb-3 custom-input">
                    <label class="form-label bold-12 text-gray">{{
                      $t("manufacturingGrant.dayLengths")
                    }}</label>
                    <div class="row g-2">
                      <div
                        v-for="day in generateOptions.day_lengths_hours"
                        :key="day.key"
                        class="col-4 col-md-3"
                      >
                        <input
                          type="number"
                          min="0"
                          step="0.5"
                          class="form-control"
                          v-model.number="day.value"
                        />
                        <small class="text-muted"
                          >{{ $t(`manufacturingGrant.${day.key}`) }} ({{
                            $t("manufacturingGrant.hours")
                          }})</small
                        >
                      </div>
                    </div>
                  </div>
                  <div class="row mb-3 custom-input">
                    <div class="col-md-4">
                      <label class="form-label bold-12 text-gray">{{
                        $t("manufacturingGrant.splitThreshold")
                      }}</label>
                      <input
                        type="number"
                        min="0"
                        class="form-control"
                        v-model.number="generateOptions.split_threshold"
                      />
                    </div>
                    <div class="col-md-4">
                      <label class="form-label bold-12 text-gray">{{
                        $t("manufacturingGrant.minimumTaskLength")
                      }}</label>
                      <input
                        type="number"
                        min="1"
                        class="form-control"
                        v-model.number="generateOptions.minimum_task_length"
                      />
                    </div>
                    <div class="col-md-4">
                      <label class="form-label bold-12 text-gray">{{
                        $t("manufacturingGrant.maxTaskSplits")
                      }}</label>
                      <input
                        type="number"
                        min="0"
                        max="3"
                        class="form-control"
                        v-model.number="generateOptions.max_task_splits"
                      />
                    </div>
                  </div>
                  <div class="mb-3 custom-input">
                    <label class="form-label bold-12 text-gray">{{
                      $t("manufacturingGrant.schedulingMode")
                    }}</label>
                    <div class="glass-radio-group">
                      <input
                        type="radio"
                        name="mode"
                        id="mode1"
                        value="REGENERATE_ALL"
                        v-model="generateOptions.mode"
                      />
                      <label class="glass-radio" for="mode1">
                        {{ $t("manufacturingGrant.modeRegenerateAll") }}
                      </label>
                      <input
                        type="radio"
                        name="mode"
                        id="mode2"
                        value="ADD_NEW"
                        v-model="generateOptions.mode"
                      />
                      <label class="glass-radio" for="mode2">
                        {{ $t("manufacturingGrant.modeAddNew") }}
                      </label>
                      <input
                        type="radio"
                        name="mode"
                        id="mode3"
                        value="FREEZE_WINDOW"
                        v-model="generateOptions.mode"
                      />
                      <label class="glass-radio" for="mode3">
                        {{ $t("manufacturingGrant.modeFreezeWindow") }}
                      </label>
                    </div>
                    <div class="mt-2 text-muted-8 small italic-text">
                      <template
                        v-if="generateOptions.mode === 'REGENERATE_ALL'"
                      >
                        {{ $t("manufacturingGrant.modeRegenerateAllHelp") }}
                      </template>
                      <template v-else-if="generateOptions.mode === 'ADD_NEW'">
                        {{ $t("manufacturingGrant.modeAddNewHelp") }}
                      </template>
                      <template
                        v-else-if="generateOptions.mode === 'FREEZE_WINDOW'"
                      >
                        {{ $t("manufacturingGrant.modeFreezeWindowHelp") }}
                      </template>
                    </div>
                  </div>
                  <div class="mb-3 custom-input whitebg-input">
                    <label class="form-label bold-12 text-gray">{{
                      $t("manufacturingGrant.groupVariantsThreshold")
                    }}</label>
                    <div class="fix-end-input">
                      <input
                        type="number"
                        class="form-control"
                        v-model.number="
                          generateOptions.group_variants_threshold
                        "
                        min="0"
                        placeholder="0"
                      />
                      <span>{{ $t("manufacturingGrant.minutes") }}</span>
                    </div>
                    <div class="mt-1 text-muted-8 small italic-text">
                      {{ $t("manufacturingGrant.groupVariantsThresholdHelp") }}
                    </div>
                  </div>
                </div>
                <div class="modal-footer">
                  <button
                    type="button"
                    class="btn btn-secondary"
                    data-bs-dismiss="modal"
                  >
                    {{ $t("manufacturingGrant.cancel") }}
                  </button>
                  <button
                    type="button"
                    class="btn btn-primary"
                    @click="confirmGenerate"
                  >
                    {{ $t("manufacturingGrant.start") }}
                  </button>
                </div>
              </div>
            </div>
          </div>
          <!-- NAPI NÉZET -->
          <div class="day-view">
            <div v-if="loading" class="p-5 text-center w-100">
              <div class="spinner-border text-primary mb-3" role="status"></div>
              <h5>{{ $t("manufacturingGrant.loading") }}</h5>
            </div>
            <div v-else-if="list.length === 0" class="p-5 text-center w-100">
              <h5>{{ $t("manufacturingGrant.noData") }}</h5>
              <p>{{ $t("manufacturingGrant.noDataHint") }}</p>
            </div>
            <template v-else>
              <div class="day-head">
                <div class="day-head-title bold-12">{{ viewLabel }}</div>
                <div class="day-ruler" ref="dayRuler">
                  <div class="day-hour" v-for="h in dayHours" :key="h">
                    {{ h }}
                  </div>
                </div>
              </div>
              <div class="day-body">
                <div class="day-item" v-for="row in dayRows" :key="row.id">
                  <div class="day-title">
                    <div
                      class="tl-avatar"
                      :class="'avatar-' + (row.type || '').toLowerCase()"
                    >
                      {{ initials(row.name) }}
                    </div>
                    <div class="day-title-sep"></div>
                    <div class="flex-fill" style="min-width: 0">
                      <div
                        class="d-flex align-items-center gap-2"
                        style="min-width: 0"
                      >
                        <span class="semibold-12 text-truncate">{{
                          row.name
                        }}</span>
                        <span v-if="row.onLeave" class="tl-leave-badge">{{
                          $t("manufacturingGrant.onLeave")
                        }}</span>
                      </div>
                      <div class="regular-10 text-gray text-truncate">
                        {{ row.secondary_label }}
                      </div>
                      <div class="d-flex align-items-center gap-2 mt-1">
                        <div class="tl-load">
                          <div
                            :style="{
                              width: row.loadPct + '%',
                              background: row.loadColor,
                            }"
                          ></div>
                        </div>
                        <span
                          class="tl-load-label"
                          :style="{ color: row.loadColor }"
                        >
                          {{ row.loadLabel }}
                        </span>
                      </div>
                    </div>
                  </div>
                  <div class="day-row">
                    <div class="day-grid">
                      <div v-for="h in dayHours" :key="h"></div>
                    </div>
                    <div v-if="row.onLeave" class="day-leave semibold-12">
                      {{ $t("manufacturingGrant.notWorkingToday") }}
                    </div>
                    <div
                      v-else-if="!row.times.length"
                      class="day-empty regular-10 text-gray"
                    >
                      {{ $t("manufacturingGrant.noOpsToday") }}
                    </div>
                    <div
                      class="timeline-line"
                      v-for="line in row.times"
                      :key="line.id"
                      :class="[
                        dayBarSizeClass(line),
                        { highlighted: isHighlighted(line) },
                      ]"
                      :style="{
                        left: dayPct(line.start_time) + '%',
                        width: dayBarWidthPct(line) + '%',
                        background: line.color || '#90daf0',
                      }"
                      :title="dayBarTitle(line)"
                      @click="showDetail(line)"
                    >
                      <span
                        class="timeline-color"
                        v-if="dayBarSizeClass(line) === 'bar-xs'"
                      ></span>
                      <template v-else>
                        <div class="tl-bar">
                          <span class="tl-icon"><BaseIcon name="box" /></span>
                          <span class="tl-name">{{ line.name }}</span>
                          <span class="tl-qty" v-if="line.quantity != null"
                            >&times;{{ line.quantity }}</span
                          >
                        </div>
                        <div class="tl-line2">
                          <span
                            class="tl-serial"
                            v-if="line?.product?.serial"
                            >{{ line.product.serial }}</span
                          >
                          <span
                            class="tl-chars"
                            v-if="line.characteristics?.length"
                          >
                            <span
                              class="tl-char"
                              v-for="c in line.characteristics"
                              :key="c.type_id"
                              >{{ c.value }}</span
                            >
                          </span>
                          <span class="tl-end">{{ dayTimeLabel(line) }}</span>
                        </div>
                        <div
                          class="tl-progress"
                          v-if="line.done_percent != null"
                        >
                          <div class="tl-track">
                            <div
                              class="tl-fill"
                              :style="{
                                width: line.done_percent + '%',
                                background: progressColor(line.done_percent),
                              }"
                            ></div>
                          </div>
                          <span class="tl-pct">{{ line.done_percent }}%</span>
                        </div>
                      </template>
                    </div>
                  </div>
                </div>
                <div
                  class="day-now"
                  v-if="dayNowPct !== null"
                  :style="{
                    left:
                      'calc(300px + (100% - 300px) * ' + dayNowPct / 100 + ')',
                  }"
                ></div>
              </div>
            </template>
          </div>
        </div>
      </div>
    </div>
    <div ref="detailModal" class="modal fade" tabindex="-1" aria-hidden="true">
      <div
        class="
          modal-dialog modal-xl modal-dialog-centered modal-dialog-scrollable
        "
      >
        <div class="modal-content">
          <div class="modal-header">
            <div class="flex-fill" style="min-width: 0">
              <h5 class="modal-title bold-19 text-truncate">
                {{
                  selectedLine?.name ||
                  $t("manufacturingGrant.operationDetails")
                }}
              </h5>
              <div class="regular-12 text-muted text-truncate">
                <span v-if="selectedLine?.worker">{{
                  selectedLine.worker
                }}</span>
                <span
                  v-if="selectedLine?.worker && selectedLine?.product?.serial"
                >
                  ·
                </span>
                <span v-if="selectedLine?.product?.serial">{{
                  selectedLine.product.serial
                }}</span>
              </div>
            </div>
            <div class="d-flex align-items-center gap-3">
              <div class="text-end" v-if="selectedLine">
                <div class="regular-10 text-muted">
                  {{ $t("manufacturingGrant.startTime") }} /
                  {{ $t("manufacturingGrant.endTime") }}
                </div>
                <div class="semibold-12">
                  {{
                    selectedLine ? shortTime(selectedLine.start_time) : "–"
                  }}&ndash;{{
                    selectedLine ? shortTime(selectedLine.end_time) : "–"
                  }}
                  <span v-if="selectedLine" class="text-muted">
                    ·
                    {{
                      durationLabel(
                        selectedLine.start_time,
                        selectedLine.end_time
                      )
                    }}
                  </span>
                </div>
              </div>
              <button class="btn-close" data-bs-dismiss="modal"></button>
            </div>
          </div>
          <div v-if="!selectedLine" class="modal-body text-center py-5">
            <div class="spinner-border text-primary mb-3" role="status"></div>
            <h5 class="bold-16 text-muted">
              {{ $t("manufacturingGrant.loading") }}
            </h5>
          </div>
          <div
            v-else
            class="modal-body"
            style="max-height: 65vh; overflow: hidden"
          >
            <div class="row g-4 h-100">
              <!-- LEFT: Product info -->
              <div
                class="col-3"
                style="
                  max-height: calc(65vh - 2rem);
                  overflow-y: auto;
                  overflow-x: hidden;
                "
              >
                <div class="bold-12 text-muted mb-2">
                  {{ $t("manufacturingGrant.product") }}
                </div>
                <div
                  class="white-box p-0 mb-3"
                  style="
                    aspect-ratio: 1;
                    overflow: hidden;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    background: var(--extra-light);
                  "
                >
                  <img
                    v-if="productImageUrl"
                    :src="productImageUrl"
                    alt=""
                    style="width: 100%; height: 100%; object-fit: contain"
                  />
                  <span v-else class="text-muted" style="font-size: 3rem"
                    >👗</span
                  >
                </div>
                <div class="bold-16">
                  {{
                    selectedLine.product?.product_translations?.[0]?.name ||
                    selectedLine.product?.serial ||
                    "–"
                  }}
                </div>
                <div class="regular-12 text-muted mt-1">
                  {{ selectedLine.product?.serial || "" }}
                </div>

                <hr class="my-3" />
                <div
                  class="
                    bold-12
                    text-muted
                    mb-2
                    d-flex
                    align-items-center
                    gap-2
                  "
                >
                  <span>{{ $t("manufacturingGrant.team") }}</span>
                  <span v-if="opWorkers.length" class="op-access-count">{{
                    $t("manufacturingGrant.canDoIt", { n: opWorkers.length })
                  }}</span>
                </div>
                <div v-if="opWorkers.length" class="d-flex flex-column gap-1">
                  <div
                    v-for="w in opWorkers"
                    :key="w.id"
                    class="op-access-row"
                    :class="{ assigned: w.assigned }"
                  >
                    <span class="op-access-av">{{ initials(w.name) }}</span>
                    <span class="semibold-14 text-truncate">{{ w.name }}</span>
                    <span v-if="w.assigned" class="op-access-badge">{{
                      $t("manufacturingGrant.assigned")
                    }}</span>
                  </div>
                </div>
                <div
                  v-else-if="selectedLine.worker"
                  class="op-access-row assigned"
                >
                  <span class="op-access-av">{{
                    initials(selectedLine.worker)
                  }}</span>
                  <span class="semibold-14 text-truncate">{{
                    selectedLine.worker
                  }}</span>
                </div>
                <div v-else class="regular-12 text-muted mb-2">–</div>

                <div
                  class="
                    bold-12
                    text-muted
                    mb-2
                    mt-3
                    d-flex
                    align-items-center
                    gap-2
                  "
                >
                  <span>{{ $t("manufacturingGrant.machine") }}</span>
                  <span v-if="opMachines.length" class="op-access-count">{{
                    $t("manufacturingGrant.canDoIt", { n: opMachines.length })
                  }}</span>
                </div>
                <div v-if="opMachines.length" class="d-flex flex-column gap-1">
                  <div
                    v-for="m in opMachines"
                    :key="m.id"
                    class="op-access-row"
                    :class="{ assigned: m.assigned }"
                  >
                    <span class="op-access-av machine">
                      <BaseIcon name="scissor" />
                    </span>
                    <span class="semibold-14 text-truncate">{{ m.name }}</span>
                    <span v-if="m.assigned" class="op-access-badge">{{
                      $t("manufacturingGrant.assigned")
                    }}</span>
                  </div>
                </div>
                <div
                  v-else-if="selectedLine.machine"
                  class="op-access-row assigned"
                >
                  <span class="op-access-av machine">
                    <BaseIcon name="scissor" />
                  </span>
                  <span class="semibold-14 text-truncate">{{
                    selectedLine.machine
                  }}</span>
                </div>
              </div>

              <!-- MIDDLE: Operation details -->
              <div class="col-5">
                <div class="bold-11 text-muted op-section-label">
                  {{ $t("manufacturingGrant.currentOperation") }}
                </div>
                <div
                  v-if="
                    selectedSchedule?.product_manufacturing_operation
                      ?.description
                  "
                  class="regular-14 text-muted mb-3"
                >
                  {{
                    selectedSchedule.product_manufacturing_operation.description
                  }}
                </div>
                <div v-else class="regular-14 text-muted mb-3">
                  {{ $t("manufacturingGrant.noDescription") }}
                </div>

                <div class="row g-2 mb-3">
                  <div class="col-4">
                    <div
                      class="rounded p-3 text-center"
                      style="background: var(--default-04)"
                    >
                      <div class="bold-24" style="color: var(--default)">
                        {{
                          selectedSchedule?.product_manufacturing_operation
                            ?.time
                            ? selectedSchedule.product_manufacturing_operation
                                .time + "s"
                            : "–"
                        }}
                      </div>
                      <div class="bold-11 text-muted mt-1">
                        {{ $t("manufacturingGrant.unitTime") }}
                      </div>
                    </div>
                  </div>
                  <div class="col-4">
                    <div
                      class="rounded p-3 text-center"
                      style="background: #f0f0ff"
                    >
                      <div class="bold-24" style="color: var(--default)">
                        {{
                          selectedSchedule?.quantity ??
                          selectedSchedule?.item_operation?.quantity ??
                          "–"
                        }}
                      </div>
                      <div class="bold-11 text-muted mt-1">
                        {{ $t("manufacturingGrant.quantity") }}
                      </div>
                    </div>
                  </div>
                  <div class="col-4">
                    <div
                      class="rounded p-3 text-center"
                      style="background: var(--lgreen)"
                    >
                      <div class="bold-24" style="color: var(--green)">
                        {{ totalOpHours }}
                      </div>
                      <div class="bold-11 text-muted mt-1">
                        {{ $t("manufacturingGrant.totalTime") }}
                      </div>
                    </div>
                  </div>
                </div>

                <div class="bold-12 text-muted mb-2">
                  {{ $t("manufacturingGrant.characteristics") }}
                </div>
                <div
                  v-if="selectedLine.characteristics?.length"
                  class="d-flex flex-column gap-1"
                >
                  <div
                    v-for="c in selectedLine.characteristics"
                    :key="c.type_id"
                    class="
                      d-flex
                      justify-content-between
                      align-items-center
                      gap-3
                      p-2
                      rounded
                    "
                    style="background: var(--bg)"
                  >
                    <span class="semibold-12 text-muted">{{
                      c.name || c.type_id
                    }}</span>
                    <span class="semibold-14">{{ c.value }}</span>
                  </div>
                </div>
                <div v-else class="regular-12 text-muted">–</div>

                <hr class="my-3" v-if="siblingVariants.length" />
                <div
                  v-if="siblingVariants.length"
                  class="bold-12 text-muted mb-2"
                >
                  {{ $t("manufacturingGrant.otherVariants") }}
                </div>
                <div
                  v-if="siblingVariants.length"
                  class="d-flex flex-column gap-1"
                >
                  <div
                    v-for="sv in siblingVariants"
                    :key="sv.variantId"
                    class="
                      d-flex
                      align-items-start
                      gap-2
                      p-2
                      rounded
                      cursor-pointer
                    "
                    :style="{
                      background: sv.isCurrent
                        ? 'var(--default-04)'
                        : 'var(--bg)',
                      border: sv.isCurrent
                        ? '1px solid var(--default)'
                        : 'none',
                    }"
                    @click="
                      !sv.isCurrent && showDetailBySchedule(sv.scheduleItem)
                    "
                  >
                    <div class="flex-fill">
                      <div
                        class="semibold-12"
                        :class="sv.isCurrent ? 'text-primary' : ''"
                      >
                        {{ sv.product?.serial || "#" + sv.variantId }}
                      </div>
                      <div
                        v-if="sv.characteristics?.length"
                        class="regular-10 text-muted mt-1"
                      >
                        <span
                          v-for="(c, ci) in sv.characteristics"
                          :key="c.type_id"
                        >
                          {{ c.value
                          }}<span v-if="ci < sv.characteristics.length - 1"
                            >,
                          </span>
                        </span>
                      </div>
                      <div class="d-flex gap-3 mt-1">
                        <span class="regular-10 text-muted"
                          >{{ $t("manufacturingGrant.quantity") }}:
                          {{ sv.quantity }}</span
                        >
                        <span class="regular-10 text-muted"
                          >{{ $t("manufacturingGrant.totalTime") }}:
                          {{ sv.totalTime }}</span
                        >
                      </div>
                    </div>
                    <span
                      v-if="!sv.isCurrent"
                      class="text-primary semibold-12 text-nowrap"
                      style="font-size: 0.7rem; margin-top: 2px"
                      >{{ $t("manufacturingGrant.view") }}</span
                    >
                    <span
                      v-else
                      class="semibold-12 text-nowrap"
                      style="
                        font-size: 0.7rem;
                        margin-top: 2px;
                        color: var(--default);
                      "
                      >{{ $t("manufacturingGrant.current") }}</span
                    >
                  </div>
                </div>
              </div>

              <!-- RIGHT: Operations block with +/- done -->
              <div
                class="col-4"
                style="
                  max-height: calc(65vh - 2rem);
                  overflow-y: auto;
                  overflow-x: hidden;
                "
              >
                <div
                  class="d-flex align-items-center justify-content-between mb-2"
                >
                  <div class="bold-12 text-muted">
                    {{ $t("manufacturingGrant.operations") }}
                  </div>
                  <div class="regular-10 text-muted">
                    {{
                      hasChanges
                        ? $t("manufacturingGrant.pendingChanges", {
                            n: pendingCount,
                          })
                        : $t("manufacturingGrant.noPendingChanges")
                    }}
                  </div>
                </div>
                <div v-if="isLoadingOps" class="text-center py-3">
                  <div
                    class="spinner-border spinner-border-sm text-primary"
                  ></div>
                </div>
                <div
                  v-else-if="modalSteps.length"
                  class="d-flex flex-column gap-2"
                >
                  <template v-for="(step, si) in modalSteps" :key="step.id">
                    <div
                      v-if="modalHasGroups && step.isGroupStart"
                      class="op-group-head"
                    >
                      <span class="bold-11 text-muted">{{
                        $t("manufacturingGrant.groupLabel", { n: step.group })
                      }}</span>
                      <span class="op-group-line"></span>
                      <span class="regular-10 text-muted">{{
                        $t("manufacturingGrant.groupOps", { n: step.groupSize })
                      }}</span>
                    </div>
                    <div
                      class="rounded p-2 cursor-pointer"
                      :class="[
                        step.id === currentOpId ? 'step-active' : 'step-item',
                      ]"
                      :style="
                        modalHasGroups && step.sub > 1
                          ? { marginLeft: '14px' }
                          : null
                      "
                      @click="goToStep(step)"
                    >
                      <div class="d-flex align-items-center gap-2 mb-1">
                        <span
                          class="op-ord"
                          :class="
                            step.id === currentOpId
                              ? 'is-current'
                              : doneVal(step.id) >= modalTotal
                              ? 'is-done'
                              : ''
                          "
                          >{{
                            modalHasGroups
                              ? step.code
                              : ordinalSuffix(step.order)
                          }}</span
                        >
                        <span
                          class="semibold-12 flex-fill"
                          :class="step.id === currentOpId ? 'text-white' : ''"
                          >{{ step.name }}</span
                        >
                        <span
                          v-if="step.id === currentOpId"
                          class="op-current-badge"
                          >{{ $t("manufacturingGrant.current") }}</span
                        >
                        <span
                          class="semibold-12 text-nowrap"
                          :class="
                            step.id === currentOpId
                              ? 'text-white-50'
                              : 'text-muted'
                          "
                          style="font-size: 0.7rem"
                          >{{
                            step.time
                              ? Math.round((step.time * modalTotal) / 60) + "m"
                              : ""
                          }}</span
                        >
                      </div>
                      <div
                        v-if="step.description"
                        class="regular-10 mb-1"
                        :class="
                          step.id === currentOpId
                            ? 'text-white-50'
                            : 'text-muted'
                        "
                        style="line-height: 1.3"
                      >
                        {{ step.description }}
                      </div>
                      <div class="d-flex align-items-center gap-2 mt-1">
                        <span
                          class="semibold-12 text-nowrap"
                          :class="step.id === currentOpId ? 'text-white' : ''"
                          >{{ $t("manufacturingGrant.done") }}:
                          {{ doneVal(step.id) }} / {{ modalTotal }}</span
                        >
                        <div class="st-track">
                          <div
                            class="st-fill"
                            :style="{
                              width:
                                (modalTotal
                                  ? Math.min(
                                      100,
                                      (doneVal(step.id) / modalTotal) * 100
                                    )
                                  : 0) + '%',
                            }"
                          ></div>
                        </div>
                      </div>
                      <div
                        class="d-flex gap-1 mt-2"
                        v-if="doneVal(step.id) < modalTotal"
                        @click.stop
                      >
                        <button
                          class="btn btn-sm flex-fill st-btn"
                          :class="
                            step.id === currentOpId
                              ? 'btn-light'
                              : 'btn-outline-primary'
                          "
                          @click="addNumber(step.id, 1)"
                          :disabled="
                            doneVal(step.id) >= modalTotal ||
                            (si > 0 &&
                              doneVal(step.id) >=
                                doneVal(modalSteps[si - 1].id))
                          "
                        >
                          +1
                        </button>
                        <button
                          class="btn btn-sm flex-fill st-btn"
                          :class="
                            step.id === currentOpId
                              ? 'btn-light'
                              : 'btn-outline-primary'
                          "
                          @click="addNumber(step.id, 5)"
                          :disabled="
                            doneVal(step.id) >= modalTotal ||
                            (si > 0 &&
                              doneVal(step.id) >=
                                doneVal(modalSteps[si - 1].id))
                          "
                        >
                          +5
                        </button>
                        <button
                          class="btn btn-sm flex-fill st-btn"
                          :class="
                            step.id === currentOpId
                              ? 'btn-light'
                              : 'btn-outline-primary'
                          "
                          @click="addNumber(step.id, addMaxI(si))"
                          :disabled="!showAddBtn(si)"
                        >
                          +{{ addMaxI(si) }}
                        </button>
                      </div>
                      <div
                        v-else
                        class="d-flex align-items-center gap-1 mt-2 semibold-12"
                        :class="
                          step.id === currentOpId
                            ? 'text-white'
                            : 'text-success'
                        "
                      >
                        <BaseIcon name="check" />
                        {{ $t("manufacturingGrant.completed") }}
                      </div>
                      <div
                        v-if="
                          doneVal(step.id) < modalTotal &&
                          si > 0 &&
                          doneVal(step.id) >= doneVal(modalSteps[si - 1].id)
                        "
                        class="regular-10 mt-1"
                        :class="
                          step.id === currentOpId
                            ? 'text-white-50'
                            : 'text-muted'
                        "
                      >
                        {{ $t("manufacturingGrant.blockedByPrevious") }}
                      </div>
                    </div>
                  </template>
                </div>
                <div v-else class="regular-12 text-muted py-3 text-center">
                  {{ $t("manufacturingGrant.noOperations") }}
                </div>
              </div>
            </div>
          </div>
          <div class="modal-footer" v-if="selectedLine">
            <button
              type="button"
              class="btn btn-secondary"
              data-bs-dismiss="modal"
            >
              {{ $t("manufacturingGrant.close") }}
            </button>
            <button
              type="button"
              class="btn btn-primary"
              @click="saveOpsDone"
              :disabled="!hasChanges || modalSaving"
            >
              <span
                v-if="modalSaving"
                class="spinner-border spinner-border-sm me-1"
              ></span>
              {{ $t("manufacturingGrant.save") }}
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import dayjs from "dayjs";
import localizedFormat from "dayjs/plugin/localizedFormat";
import relativeTime from "dayjs/plugin/relativeTime";
import { mapState } from "vuex";
import http from "../../modules/http";
import ws from "../../modules/ws";
import tenant from "../../modules/tenant";
import BaseIcon from "@/components/icons/BaseIcon";
import TimePicker from "@/components/inputs/TimePicker";

const bootstrap = require("bootstrap");

const DAY_WINDOW_START = 6; // óra — a napi nézet alap ablaka (a műveletek tágíthatják)
const DAY_WINDOW_END = 18;

export default {
  name: "ManufacturingGrant",
  components: { BaseIcon, TimePicker },
  data() {
    return {
      loading: true,
      day: dayjs().format("YYYY-MM-DD"),
      dayTrackWidth: 1000,
      // rejtett műveletek (név szerint, kisbetűsen) — alapból a Szabászat
      hiddenOps: (() => {
        try {
          const v = JSON.parse(
            localStorage.getItem("productionPlan.hiddenOps")
          );
          return Array.isArray(v) ? v : ["szabászat"];
        } catch (e) {
          return ["szabászat"];
        }
      })(),
      generating: false,
      errorMessage: "",
      viewType: 1,
      dayInfo: {},
      stats: {
        total_operations: 0,
        total_items: 0,
        total_machines: 0,
        total_workers: 0,
        date_start: null,
        date_end: null,
      },
      methods: [
        {
          title: this.$t
            ? this.$t("manufacturingGrant.generateSchedule")
            : "Generate Schedule",
          icon: "play-circle",
          always: true,
          method: () => {
            if (!this.generateModal) {
              this.generateModal = new bootstrap.Modal(
                this.$refs.generateModal
              );
            }
            this.generateModal.show();
          },
        },
        {
          title: this.$t
            ? this.$t("manufacturingGrant.productView")
            : "Product view",
          icon: "box",
          always: true,
          method: () => {
            this.viewType = 0;
          },
        },
        {
          title: this.$t ? this.$t("manufacturingGrant.userView") : "User view",
          icon: "partners",
          always: true,
          method: () => {
            this.viewType = 1;
          },
        },
        {
          title: this.$t
            ? this.$t("manufacturingGrant.machineView")
            : "Machine view",
          icon: "scissor",
          always: true,
          method: () => {
            this.viewType = 2;
          },
        },
      ],
      operationAccess: {},
      services: [],
      users: [],
      machines: [],
      selectedLine: null,
      selectedSchedule: null,
      detailModal: null,
      generateModal: null,
      modalSteps: [],
      modalHasGroups: false,
      modalDone: {},
      modalDoneNow: {},
      modalTotal: 0,
      modalItemId: null,
      modalWarehouseOpId: null,
      modalSaving: false,
      isLoadingOps: false,
      generateOptions: {
        mode: "REGENERATE_ALL",
        policy: "UIS",
        shift_start_time: "09:00",
        shift_length_hours: 8,
        break_length_hours: 16,
        group_variants_threshold: 0,
        inner_break_duration: 30,
        inner_break_start_time: "12:00",
        inner_break_end_time: "12:45",
        day_lengths_hours: [
          { key: "monday", value: 8.5 },
          { key: "tuesday", value: 8.5 },
          { key: "wednesday", value: 8.5 },
          { key: "thursday", value: 8.5 },
          { key: "friday", value: 6 },
          { key: "saturday", value: 0 },
          { key: "sunday", value: 0 },
        ],
        split_threshold: 180,
        minimum_task_length: 30,
        max_task_splits: 3,
      },
      now: Date.now(),
      timelineRangeStart: "",
      timelineRangeEnd: "",
    };
  },
  computed: {
    ...mapState({
      searchQuery: (state) => state.topBar?.q || "",
    }),
    warehouses() {
      return this.$store.state.warehouses;
    },
    warehouse() {
      return this.$store.state.topBar.warehouse;
    },
    selectedWarehouse() {
      return this.warehouses.find((e) => e.id === this.warehouse);
    },
    list() {
      let sourceData = this.services;

      if (this.viewType === 1) {
        sourceData = this.users.length > 0 ? this.users : this.services;
      } else if (this.viewType === 2) {
        sourceData = this.machines.length > 0 ? this.machines : this.services;
      }

      if (this.services.length > 0) {
        if (this.viewType === 1) {
          const userMap = {};
          this.services.forEach((s) => {
            const worker = s.worker;
            if (!worker) return;
            if (!userMap[worker.id]) {
              userMap[worker.id] = {
                id: worker.id,
                name: worker.name,
                type: "Worker",
                secondary_label: "",
                times: [],
              };
            }
            userMap[worker.id].times.push(this.mapScheduleToTime(s));
          });
          return Object.values(userMap).filter((s) => s.times?.length > 0);
        } else if (this.viewType === 2) {
          const machineMap = {};
          this.services.forEach((s) => {
            const asset = s.asset;
            if (!asset) return;
            if (!machineMap[asset.id]) {
              machineMap[asset.id] = {
                id: asset.id,
                name: asset.name,
                type: "Machine",
                secondary_label: asset.model,
                times: [],
              };
            }
            machineMap[asset.id].times.push(this.mapScheduleToTime(s));
          });
          return Object.values(machineMap).filter((s) => s.times?.length > 0);
        } else if (this.viewType === 0 && !this.services[0].times) {
          const productMap = {};
          this.services.forEach((s) => {
            const variant = s.item_operation?.item?.productVariant;
            const product = variant?.product;
            if (!product) return;
            const productName =
              product.serial ||
              product.product_translations?.[0]?.name ||
              "Product";
            if (!productMap[variant.id]) {
              productMap[variant.id] = {
                id: variant.id,
                name: productName,
                type: "Product",
                secondary_label: product.serial,
                times: [],
              };
            }
            productMap[variant.id].times.push(this.mapScheduleToTime(s));
          });
          return Object.values(productMap).filter((s) => s.times?.length > 0);
        }
      }

      return Array.isArray(sourceData)
        ? sourceData.filter((s) => s.times?.length > 0)
        : [];
    },
    viewLabel() {
      const labels = [
        this.$t("manufacturingGrant.productView"),
        this.$t("manufacturingGrant.userView"),
        this.$t("manufacturingGrant.machineView"),
      ];
      return labels[this.viewType] || labels[0];
    },
    totalOpHours() {
      if (!this.selectedSchedule) return "–";
      const timeSec =
        this.selectedSchedule.product_manufacturing_operation?.time || 0;
      const qty =
        this.selectedSchedule?.quantity ??
        this.selectedSchedule.item_operation?.quantity ??
        0;
      if (!timeSec || !qty) return "–";
      return ((timeSec * qty) / 3600).toFixed(1) + "h";
    },
    currentOpId() {
      return this.selectedSchedule?.product_manufacturing_operation?.id || null;
    },
    hasChanges() {
      for (const id in this.modalDoneNow) {
        if (this.modalDoneNow[id] > 0) return true;
      }
      return false;
    },
    pendingCount() {
      let n = 0;
      for (const id in this.modalDoneNow)
        n += Number(this.modalDoneNow[id]) || 0;
      return n;
    },
    productImageUrl() {
      const product = this.selectedLine?.product;
      if (!product) return null;
      const images = product.images;
      if (!images?.length) return null;
      const img =
        images.find((i) => i.pivot?.in_manufacturing) ||
        images.find((i) => i.pivot?.main) ||
        images[0];
      if (!img?.path) return null;
      const name = img.name || "";
      return (
        (this.mediaServer || "") + "/" + img.path + "/262-" + name + ".webp"
      );
    },
    siblingVariants() {
      const s = this.selectedSchedule;
      if (!s?.item_operation?.item?.productVariant) return [];
      const curVariantId = s.item_operation.item.productVariant.id;
      const curProductId = s.item_operation.item.productVariant.product?.id;
      const curPmoId = s.product_manufacturing_operation?.id;
      if (!curProductId) return [];
      const all = [];
      const curQty = s.item_operation?.quantity || 0;
      const curTime = s.product_manufacturing_operation?.time || 0;
      all.push({
        variantId: curVariantId,
        product: s.item_operation.item.productVariant.product,
        scheduleItem: s,
        characteristics: s.characteristics || [],
        quantity: curQty,
        totalTime:
          curTime && curQty
            ? ((curTime * curQty) / 3600).toFixed(1) + "h"
            : "–",
        isCurrent: true,
      });
      const seen = new Map();
      (this.services || []).forEach((item) => {
        const v = item.item_operation?.item?.productVariant;
        if (!v || v.id === curVariantId) return;
        if (v.product?.id !== curProductId) return;
        if (item.product_manufacturing_operation?.id !== curPmoId) return;
        if (!seen.has(v.id)) {
          const time = item.product_manufacturing_operation?.time || 0;
          const qty = item.item_operation?.quantity || 0;
          seen.set(v.id, {
            variantId: v.id,
            product: v.product,
            scheduleItem: item,
            characteristics: item.characteristics || [],
            quantity: qty,
            totalTime:
              time && qty ? ((time * qty) / 3600).toFixed(1) + "h" : "–",
            isCurrent: false,
          });
        }
      });
      return all
        .concat(Array.from(seen.values()))
        .sort((a, b) => a.variantId - b.variantId);
    },
    mediaServer() {
      return tenant.getAppMediaUrl();
    },
    // a művelethez jogosult dolgozók / gépek (ki VÉGEZHETI, nem ki végzi)
    currentAccess() {
      const id =
        this.selectedSchedule?.product_manufacturing_operation
          ?.manufacturing_operation_id ??
        this.selectedSchedule?.manufacturing_operation_id;
      return this.operationAccess[id] || null;
    },
    opWorkers() {
      const list = this.currentAccess?.crmItems || [];
      const assignedId = this.selectedSchedule?.worker?.id;
      return list.map((w) => ({
        id: w.id,
        name: w.name,
        assigned: assignedId != null && w.id === assignedId,
      }));
    },
    opMachines() {
      const list = this.currentAccess?.assets || [];
      const assignedId = this.selectedSchedule?.asset?.id;
      return list.map((m) => ({
        id: m.id,
        name: m.name,
        assigned: assignedId != null && m.id === assignedId,
      }));
    },
    // ── ÜTEMEZÉS-GENERÁLÁS ──────────────────────────────────────
    shiftCycleSum() {
      return (
        (Number(this.generateOptions.shift_length_hours) || 0) +
        (Number(this.generateOptions.break_length_hours) || 0)
      );
    },
    // 24-tól eltérő ciklus: a műszakkezdés naponta csúszik
    shiftCycleWarning() {
      return this.shiftCycleSum !== 24;
    },
    // hova esik a következő műszak kezdete a mostani beállítással
    nextShiftStart() {
      const start = this.timeToMinutes(this.generateOptions.shift_start_time);
      const next = (((start + this.shiftCycleSum * 60) % 1440) + 1440) % 1440;
      const h = Math.floor(next / 60);
      const m = next % 60;
      return String(h).padStart(2, "0") + ":" + String(m).padStart(2, "0");
    },
    // ── MŰVELET-SZŰRŐ ───────────────────────────────────────────
    opNames() {
      const set = new Set();
      for (const s of this.services || []) {
        const n =
          s.product_manufacturing_operation?.manufacturingOperation?.name;
        if (n) set.add(n);
      }
      for (const n of this.hiddenOps) {
        if (![...set].some((x) => x.toLowerCase() === n)) set.add(n);
      }
      return [...set].sort((a, b) => a.localeCompare(b, "hu"));
    },
    hiddenOpCount() {
      return this.hiddenOps.length;
    },
    // list() a rejtett műveletek nélkül; a csak rejtett műveletet végző sorok eltűnnek
    visibleList() {
      if (!this.hiddenOps.length) return this.list;
      return this.list
        .map((row) => ({
          ...row,
          times: (row.times || []).filter((t) => !this.isHiddenOp(t.name)),
        }))
        .filter((row) => row.times.length > 0);
    },
    // ── NAPI NÉZET ──────────────────────────────────────────────
    dayObj() {
      return dayjs(this.day).startOf("day");
    },
    intlLocale() {
      const l = this.$i18n?.locale || "en";
      return l === "hu" ? "hu-HU" : l === "ro" ? "ro-RO" : "en-GB";
    },
    dayTitle() {
      return new Intl.DateTimeFormat(this.intlLocale, {
        month: "long",
        day: "numeric",
      }).format(this.dayObj.toDate());
    },
    daySub() {
      const isToday = this.dayObj.isSame(dayjs().startOf("day"));
      const dow = new Intl.DateTimeFormat(this.intlLocale, {
        weekday: "long",
      }).format(this.dayObj.toDate());
      const info = this.dayInfo?.[this.day];
      return (
        dow.charAt(0).toUpperCase() +
        dow.slice(1) +
        " · " +
        this.dayObj.year() +
        (isToday ? " · " + this.$t("manufacturingGrant.todayLower") : "") +
        (info?.holiday
          ? " · " + (info.holiday_name || this.$t("manufacturingGrant.holiday"))
          : "")
      );
    },
    shiftHours() {
      return Number(this.generateOptions?.shift_length_hours) || 8;
    },
    // az adott nap műveletei sorokra bontva (list() + napra szűrt times)
    dayRows() {
      const d0 = this.dayObj;
      const d1 = d0.add(1, "day");
      const info = this.dayInfo?.[this.day] || {};
      const leave = Array.isArray(info.on_leave) ? info.on_leave : [];
      return this.visibleList.map((row) => {
        const onLeave = leave.some(
          (x) =>
            x === row.id ||
            x === row.name ||
            x?.id === row.id ||
            x?.name === row.name ||
            x?.worker_id === row.id
        );
        const times = (row.times || []).filter((t) => {
          const st = dayjs(t.start_time);
          const en = dayjs(t.end_time);
          return st.isBefore(d1) && en.isAfter(d0);
        });
        let minutes = 0;
        for (const t of times) {
          const st = dayjs(t.start_time);
          const a = st.isBefore(d0) ? d0 : st;
          const enRaw = dayjs(t.end_time);
          const b = enRaw.isAfter(d1) ? d1 : enRaw;
          minutes += Math.max(0, b.diff(a, "minute"));
        }
        const load = minutes / 60;
        const cap = this.shiftHours;
        return {
          ...row,
          times,
          onLeave,
          loadNum: load,
          loadPct: Math.min(100, (load / cap) * 100),
          loadLabel: onLeave
            ? "–"
            : load.toFixed(1).replace(".", ",") +
              " / " +
              cap +
              " " +
              this.$t("manufacturingGrant.hoursShort"),
          loadColor:
            load > cap * 1.02
              ? "var(--red)"
              : load >= cap * 0.75
              ? "var(--green)"
              : "var(--yellow)",
        };
      });
    },
    // óra-ablak: 6–18, de a műveletek kitágítják
    dayWindow() {
      let startH = DAY_WINDOW_START;
      let endH = DAY_WINDOW_END;
      const d0 = this.dayObj;
      for (const row of this.dayRows) {
        for (const t of row.times) {
          const st = dayjs(t.start_time);
          const en = dayjs(t.end_time);
          if (st.isAfter(d0)) startH = Math.min(startH, st.hour());
          if (en.isBefore(d0.add(1, "day")))
            endH = Math.max(endH, en.minute() > 0 ? en.hour() + 1 : en.hour());
        }
      }
      endH = Math.min(24, Math.max(endH, startH + 1));
      return { startMin: startH * 60, endMin: endH * 60 };
    },
    dayHours() {
      const out = [];
      for (
        let h = this.dayWindow.startMin / 60;
        h < this.dayWindow.endMin / 60;
        h++
      ) {
        out.push((h < 10 ? "0" : "") + h + ":00");
      }
      return out;
    },
    weekDays() {
      const monday = this.dayObj.subtract((this.dayObj.day() + 6) % 7, "day");
      const today = dayjs().startOf("day");
      const dowFmt = new Intl.DateTimeFormat(this.intlLocale, {
        weekday: "short",
      });
      const out = [];
      for (let i = 0; i < 7; i++) {
        const d = monday.add(i, "day");
        const d1 = d.add(1, "day");
        let ops = 0;
        let minutes = 0;
        for (const row of this.visibleList) {
          for (const t of row.times || []) {
            const st = dayjs(t.start_time);
            const en = dayjs(t.end_time);
            if (st.isBefore(d1) && en.isAfter(d)) {
              ops++;
              const a = st.isBefore(d) ? d : st;
              const b = en.isAfter(d1) ? d1 : en;
              minutes += Math.max(0, b.diff(a, "minute"));
            }
          }
        }
        const cap = Math.max(1, this.visibleList.length) * this.shiftHours;
        out.push({
          date: d.format("YYYY-MM-DD"),
          dayNum: d.date(),
          dow: dowFmt.format(d.toDate()).replace(".", "").toUpperCase(),
          opsLabel: ops + " " + this.$t("manufacturingGrant.opsShort"),
          loadPct: Math.min(100, (minutes / 60 / cap) * 100),
          isToday: d.isSame(today),
          isSelected: d.isSame(this.dayObj),
          isHoliday: !!this.dayInfo?.[d.format("YYYY-MM-DD")]?.holiday,
          onLeaveCount: (this.dayInfo?.[d.format("YYYY-MM-DD")]?.on_leave || [])
            .length,
        });
      }
      return out;
    },
    dayStats() {
      const rows = this.dayRows;
      const totalOps = rows.reduce((a, r) => a + r.times.length, 0);
      const items = rows.reduce(
        (a, r) =>
          a + r.times.reduce((x, t) => x + (Number(t.quantity) || 0), 0),
        0
      );
      const active = rows.filter((r) => r.times.length > 0).length;
      const hours = rows.reduce((a, r) => a + r.loadNum, 0);
      const capSum = Math.max(1, rows.length * this.shiftHours);
      const util = hours / capSum;
      const rowLabel =
        this.viewType === 2
          ? this.$t("manufacturingGrant.machine")
          : this.viewType === 0
          ? this.$t("manufacturingGrant.product")
          : this.$t("manufacturingGrant.worker");
      return [
        {
          label: this.$t("manufacturingGrant.operations"),
          value: String(totalOps),
        },
        { label: this.$t("manufacturingGrant.pieces"), value: String(items) },
        { label: rowLabel, value: active + " / " + rows.length },
        {
          label: this.$t("manufacturingGrant.plannedHours"),
          value:
            hours.toFixed(1).replace(".", ",") +
            " " +
            this.$t("manufacturingGrant.hoursShort"),
        },
        {
          label: this.$t("manufacturingGrant.utilization"),
          value: Math.round(util * 100) + "%",
          color: util > 0.95 ? "var(--red)" : "var(--green)",
        },
      ];
    },
    dayNowPct() {
      const now = dayjs(this.now);
      if (!now.startOf("day").isSame(this.dayObj)) return null;
      const min = now.hour() * 60 + now.minute();
      if (min < this.dayWindow.startMin || min > this.dayWindow.endMin)
        return null;
      return this.dayMinutesToPct(min);
    },
  },
  watch: {
    warehouses() {
      this.loadData();
    },
    warehouse() {
      this.loadData();
    },
    day() {
      if (this.ensureDayRange()) this.loadData();
    },
  },
  updated() {
    this.measureDayTrack();
  },
  mounted() {
    this.loadData();
    this.loadOperationAccess();
    ws.init();
    ws.subscribe("modify", "productionSchedule", () => {
      this.loadData();
      this.generating = false;
    });
    this._dayResize = () => this.measureDayTrack();
    window.addEventListener("resize", this._dayResize);
    this._nowTimer = setInterval(() => {
      this.now = Date.now();
    }, 60000);
  },
  unmounted() {
    window.removeEventListener("scroll", this.handleScroll);
    if (this._dayResize) window.removeEventListener("resize", this._dayResize);
    if (this._nowTimer) clearInterval(this._nowTimer);
  },
  methods: {
    // Ki végezheti az egyes műveleteket (dolgozók + gépek) — egy hívás, id szerinti térkép
    loadOperationAccess() {
      http
        .fetch("/manufacturing/operations/all")
        .then((data) => {
          const list = Array.isArray(data) ? data : data?.data || [];
          const map = {};
          for (const op of list) {
            map[op.id] = {
              crmItems: op.crmItems || [],
              assets: op.assets || [],
            };
          }
          this.operationAccess = map;
        })
        .catch(() => {
          this.operationAccess = {};
        });
    },
    // ── MŰVELET-SZŰRŐ ───────────────────────────────────────────
    isHiddenOp(name) {
      return this.hiddenOps.includes(String(name || "").toLowerCase());
    },
    toggleOp(name) {
      const key = String(name).toLowerCase();
      const i = this.hiddenOps.indexOf(key);
      if (i > -1) this.hiddenOps.splice(i, 1);
      else this.hiddenOps.push(key);
      localStorage.setItem(
        "productionPlan.hiddenOps",
        JSON.stringify(this.hiddenOps)
      );
    },
    showAllOps() {
      this.hiddenOps = [];
      localStorage.setItem("productionPlan.hiddenOps", "[]");
    },
    shiftDay(n) {
      this.day = this.dayObj.add(n, "day").format("YYYY-MM-DD");
    },
    goToday() {
      this.day = dayjs().format("YYYY-MM-DD");
    },
    selectDay(date) {
      this.day = date;
    },
    // true, ha a betöltött tartományt át kellett állítani (újratöltés kell)
    ensureDayRange() {
      const d = this.dayObj;
      const start = this.timelineRangeStart
        ? dayjs(this.timelineRangeStart)
        : null;
      const end = this.timelineRangeEnd ? dayjs(this.timelineRangeEnd) : null;
      const monday = d.subtract((d.day() + 6) % 7, "day");
      if (
        start &&
        end &&
        !monday.isBefore(start) &&
        !monday.add(7, "day").isAfter(end)
      ) {
        return false;
      }
      this.timelineRangeStart = monday.subtract(7, "day").format("YYYY-MM-DD");
      this.timelineRangeEnd = monday.add(21, "day").format("YYYY-MM-DD");
      return true;
    },
    measureDayTrack() {
      const el = this.$refs.dayRuler;
      if (!el) return;
      const w = el.offsetWidth;
      if (w && Math.abs(w - this.dayTrackWidth) > 2) this.dayTrackWidth = w;
    },
    dayMinutesToPct(min) {
      const { startMin, endMin } = this.dayWindow;
      const v = ((min - startMin) / (endMin - startMin)) * 100;
      return Math.max(0, Math.min(100, v));
    },
    dayPct(time) {
      const t = dayjs(time);
      if (t.isBefore(this.dayObj)) return 0;
      if (!t.isBefore(this.dayObj.add(1, "day"))) return 100;
      return this.dayMinutesToPct(t.hour() * 60 + t.minute());
    },
    dayBarWidthPct(line) {
      return Math.max(
        0.6,
        this.dayPct(line.end_time) - this.dayPct(line.start_time)
      );
    },
    dayBarSizeClass(line) {
      const px = (this.dayBarWidthPct(line) / 100) * this.dayTrackWidth;
      if (px < 56) return "bar-xs";
      if (px < 100) return "bar-sm";
      if (px < 160) return "bar-md";
      if (px < 300) return "bar-lg";
      return "bar-xl";
    },
    dayTimeLabel(line) {
      return (
        dayjs(line.start_time).format("HH:mm") +
        "–" +
        dayjs(line.end_time).format("HH:mm")
      );
    },
    dayBarTitle(line) {
      const parts = [line.name];
      if (line.product?.serial) parts.push(line.product.serial);
      if (line.quantity != null) parts.push(line.quantity + " db");
      if (line.characteristics?.length)
        parts.push(line.characteristics.map((c) => c.value).join(" / "));
      parts.push(
        this.dayTimeLabel(line) +
          " (" +
          this.durationLabel(line.start_time, line.end_time) +
          ")"
      );
      if (line.done_percent != null) parts.push(line.done_percent + "%");
      return parts.join(" · ");
    },
    progressColor(pct) {
      return pct >= 100
        ? "var(--green)"
        : pct > 40
        ? "var(--default)"
        : "var(--yellow)";
    },
    initials(name) {
      const p = String(name || "")
        .trim()
        .split(/\s+/)
        .filter(Boolean);
      if (!p.length) return "–";
      if (p.length === 1) return p[0].slice(0, 2).toUpperCase();
      return (p[0][0] + p[1][0]).toUpperCase();
    },
    loadData() {
      this.loading = true;
      this.errorMessage = "";
      this.ensureDayRange();
      if (!this.timelineRangeStart) {
        this.timelineRangeStart = dayjs()
          .startOf("day")
          .subtract(7, "day")
          .format("YYYY-MM-DD");
        this.timelineRangeEnd = dayjs(this.timelineRangeStart)
          .add(34, "day")
          .format("YYYY-MM-DD");
      }
      if (
        this.warehouse === null ||
        this.warehouse === undefined ||
        this.selectedWarehouse?.type !== 2
      ) {
        this.services = [];
        this.users = [];
        this.machines = [];
        this.stats = {
          total_operations: 0,
          total_items: 0,
          total_machines: 0,
          total_workers: 0,
          date_start: null,
          date_end: null,
        };
        this.dayInfo = {};
        this.loading = false;
        return;
      }
      http
        .fetch(
          "/production/plan?warehouse=" +
            this.warehouse +
            "&from=" +
            this.timelineRangeStart +
            "&to=" +
            this.timelineRangeEnd
        )
        .then((data) => {
          if (!data) return;
          this.generating = data.generating;
          this.stats = data.stats || this.stats;
          this.dayInfo = data.day_info || {};
          let schedules = data.schedule;

          if (Array.isArray(schedules)) {
            this.services = schedules;
            this.users = [];
            this.machines = [];
          } else {
            this.services = schedules.products || [];
            this.users = schedules.users || [];
            this.machines = schedules.machines || [];
          }
          this.loading = false;
        })
        .catch(() => {
          this.loading = false;
          this.errorMessage = this.$t("manufacturingGrant.loadError");
        });
    },
    handleTimelineScroll(event) {
      if (this.timelineLoadingMore) return;
      if (event.target.scrollLeft <= 300) {
        this.loadMoreTimeline("before", event.target);
      } else if (
        event.target.scrollLeft + event.target.clientWidth >=
        event.target.scrollWidth - 300
      ) {
        this.loadMoreTimeline("after", event.target);
      }
    },
    loadMoreTimeline(direction, scroller) {
      if (this.timelineLoadingMore || !this.warehouse) return;
      this.timelineLoadingMore = true;
      const previousEnd = this.timelineRangeEnd;
      const previousStart = this.timelineRangeStart;
      const previousScrollWidth = scroller?.scrollWidth || 0;
      let requestStart = previousStart;
      let requestEnd = previousEnd;
      if (direction === "before") {
        requestStart = dayjs(previousStart)
          .subtract(28, "day")
          .format("YYYY-MM-DD");
        requestEnd = previousStart;
        this.timelineRangeStart = requestStart;
      } else {
        requestStart = previousEnd;
        requestEnd = dayjs(previousEnd).add(28, "day").format("YYYY-MM-DD");
        this.timelineRangeEnd = requestEnd;
      }
      http
        .fetch(
          "/production/plan?warehouse=" +
            this.warehouse +
            "&from=" +
            requestStart +
            "&to=" +
            requestEnd
        )
        .then((data) => {
          if (!data) return;
          const schedules = data.schedule;
          const newServices = Array.isArray(schedules)
            ? schedules
            : schedules.products || [];
          const servicesById = new Map(
            (this.services || []).map((service) => [service.id, service])
          );
          for (const service of newServices) {
            servicesById.set(service.id, service);
          }
          this.services = Array.from(servicesById.values());
          this.users = Array.isArray(schedules) ? [] : schedules.users || [];
          this.machines = Array.isArray(schedules)
            ? []
            : schedules.machines || [];
          this.dayInfo = { ...this.dayInfo, ...(data.day_info || {}) };
          if (direction === "before" && scroller) {
            this.$nextTick(() => {
              scroller.scrollLeft += scroller.scrollWidth - previousScrollWidth;
            });
          }
        })
        .catch(() => {
          this.timelineRangeStart = previousStart;
          this.timelineRangeEnd = previousEnd;
        })
        .finally(() => {
          this.timelineLoadingMore = false;
        });
    },
    scrollToToday() {
      const container = this.$refs.timelineContainer;
      if (!container) return;
      const scrollTarget = this.nowLinePos - container.clientWidth / 3;
      container.scrollLeft = Math.max(0, scrollTarget);
    },
    formatDate(val) {
      if (!val) return "";
      return dayjs(val).format("MMM D, YYYY");
    },
    shortTime(val) {
      return val ? dayjs(val).format("HH:mm") : "–";
    },
    formatDateTime(val) {
      if (!val) return "";
      return dayjs(val).format("MMM D, YYYY HH:mm");
    },
    durationMinutes(start, end) {
      return dayjs(end).diff(dayjs(start), "minute");
    },
    durationLabel(start, end) {
      const min = dayjs(end).diff(dayjs(start), "minute");
      if (min < 60) return min + " min";
      const hours = Math.floor(min / 60);
      const mins = min % 60;
      if (mins === 0) return hours + "h";
      return hours + "h " + mins + "min";
    },
    date(val) {
      dayjs.locale("en");
      dayjs.extend(localizedFormat);
      dayjs.extend(relativeTime);
      return dayjs().to(dayjs(val));
    },
    isHighlighted(line) {
      if (!this.searchQuery) return false;
      const q = this.searchQuery.toLowerCase();
      const product = line?.product;
      const productName =
        product?.product_translations?.[0]?.name ||
        product?.productTranslations?.[0]?.name ||
        "";
      const customerName = line?.customer?.name || "";
      const opName = line.name || "";
      const serial = product?.serial || "";

      return (
        productName.toLowerCase().includes(q) ||
        customerName.toLowerCase().includes(q) ||
        opName.toLowerCase().includes(q) ||
        serial.toLowerCase().includes(q)
      );
    },
    opColor(s) {
      if (s.color) {
        return s.color.startsWith("#") || s.color.startsWith("hsl")
          ? s.color
          : "#" + s.color;
      }
      const opId =
        s.product_manufacturing_operation?.manufacturing_operation_id ||
        s.product_manufacturing_operation?.manufacturingOperation?.id ||
        s.id;
      const hue = ((opId * 137.508) % 360).toFixed(0);
      const sat = 55 + ((opId * 7) % 20);
      const lig = 85 + ((opId * 3) % 10);
      return `hsl(${hue}, ${sat}%, ${lig}%)`;
    },
    mapScheduleToTime(s) {
      return {
        id: s.id,
        name:
          s.product_manufacturing_operation?.manufacturingOperation?.name ||
          "Operation",
        start_time: s.start_time,
        end_time: s.end_time,
        color: this.opColor(s),
        product: s.item_operation?.item?.productVariant?.product,
        customer: s.item_operation?.warehouseOperation?.crmItem,
        description: s.product_manufacturing_operation?.description ?? "",
        machine: s.asset?.name,
        worker: s.worker?.name,
        characteristics: s.characteristics ?? [],
        quantity: s.quantity,
        itemQuantity: s.item_operation?.quantity ?? null,
        _raw: s,
      };
    },
    showDetail(line) {
      this.selectedLine = line;
      this.selectedSchedule = line?._raw || null;
      this.loadModalOps(this.selectedSchedule);
      if (!this.detailModal) {
        this.detailModal = new bootstrap.Modal(this.$refs.detailModal);
      }
      this.detailModal.show();
    },
    showDetailBySchedule(scheduleItem) {
      if (!scheduleItem) return;
      this.showDetail(this.mapScheduleToTime(scheduleItem));
    },
    loadModalOps(scheduleItem) {
      if (!scheduleItem) {
        this.modalSteps = [];
        this.modalDone = {};
        this.modalDoneNow = {};
        this.modalTotal = 0;
        return;
      }
      const variantId = scheduleItem.item_operation?.item?.productVariant?.id;
      const itemOp = scheduleItem.item_operation || {};
      this.modalItemId = itemOp.item_id;
      this.modalWarehouseOpId = itemOp.warehouse_operation_id;
      this.modalTotal = itemOp.quantity || scheduleItem?.quantity || 0;

      if (!variantId) {
        this.modalSteps = [];
        this.modalDone = {};
        this.modalDoneNow = {};
        return;
      }

      const seen = new Map();
      (this.services || []).forEach((s) => {
        const sv = s.item_operation?.item?.productVariant?.id;
        if (sv !== variantId) return;
        const pmo = s.product_manufacturing_operation;
        if (!pmo?.manufacturingOperation) return;
        const mo = pmo.manufacturingOperation;
        if (!seen.has(pmo.id)) {
          seen.set(pmo.id, {
            id: pmo.id,
            order: mo.order || pmo.order || 999,
            name: mo.name || "Operation",
            description: pmo.description || "",
            time: pmo.time || 0,
            parallel: pmo.parallel === true,
          });
        }
      });

      // Csoportok: a `parallel: true` lépés az ELŐZŐ csoporthoz tartozik
      // (ugyanaz a szabály, mint a műveletszerkesztőben) → 1/1, 1/2, 2/1 …
      const sorted = Array.from(seen.values()).sort(
        (a, b) => a.order - b.order
      );
      let group = 0;
      let sub = 0;
      sorted.forEach((step, i) => {
        if (i === 0 || !step.parallel) {
          group += 1;
          sub = 1;
        } else {
          sub += 1;
        }
        step.group = group;
        step.sub = sub;
        step.code = group + "/" + sub;
      });
      const sizes = {};
      sorted.forEach((step) => {
        sizes[step.group] = (sizes[step.group] || 0) + 1;
      });
      sorted.forEach((step) => {
        step.groupSize = sizes[step.group];
        step.isGroupStart = step.sub === 1;
      });
      // csak akkor mutatunk csoport-fejlécet, ha tényleg van párhuzamos művelet
      this.modalHasGroups = Object.values(sizes).some((n) => n > 1);
      this.modalSteps = sorted;
      this.modalDone = {};
      this.modalDoneNow = {};
      this.modalSteps.forEach((s) => {
        this.modalDoneNow[s.id] = 0;
      });

      this.isLoadingOps = false;
    },
    ordinalSuffix(i) {
      if (this.$i18n && this.$i18n.locale !== "en") return i + ".";
      let j = i % 10,
        k = i % 100;
      if (j === 1 && k !== 11) return i + "st";
      if (j === 2 && k !== 12) return i + "nd";
      if (j === 3 && k !== 13) return i + "rd";
      return i + "th";
    },
    doneVal(id) {
      return (this.modalDone[id] || 0) + (this.modalDoneNow[id] || 0);
    },
    addNumber(id, number) {
      if (!number) return;
      const now = this.modalDoneNow[id] || 0;
      this.modalDoneNow = { ...this.modalDoneNow, [id]: now + number };
    },
    addMaxI(si) {
      const step = this.modalSteps[si];
      if (!step) return 0;
      if (si === 0) return Math.max(0, this.modalTotal - this.doneVal(step.id));
      const prev = this.modalSteps[si - 1];
      return Math.max(
        0,
        Math.min(
          this.modalTotal - this.doneVal(step.id),
          this.doneVal(prev.id) - this.doneVal(step.id)
        )
      );
    },
    showAddBtn(si) {
      const step = this.modalSteps[si];
      if (!step) return false;
      if (this.doneVal(step.id) >= this.modalTotal) return false;
      if (si === 0) return this.doneVal(step.id) < this.modalTotal;
      const prev = this.modalSteps[si - 1];
      return this.doneVal(step.id) < this.doneVal(prev.id);
    },
    saveOpsDone() {
      if (!this.hasChanges) return;
      this.modalSaving = true;
      const data = {
        waste: 0,
        item_id: this.modalItemId,
        operation_id: this.modalWarehouseOpId ?? this.$route.params.id,
        done: this.modalDoneNow,
      };
      http
        .fetch("/manufacturing/operations/done", data)
        .then(() => {
          for (const id in this.modalDoneNow) {
            this.modalDone[id] =
              (this.modalDone[id] || 0) + this.modalDoneNow[id];
            this.modalDoneNow[id] = 0;
          }
          this.modalSaving = false;
        })
        .catch(() => {
          this.modalSaving = false;
        });
    },
    goToStep(step) {
      const s = this.selectedSchedule;
      if (!s?.item_operation?.item?.productVariant) return;
      const variantId = s.item_operation.item.productVariant.id;
      const target = (this.services || []).find(
        (item) =>
          item.item_operation?.item?.productVariant?.id === variantId &&
          item.product_manufacturing_operation?.id === step.id
      );
      if (target) this.showDetailBySchedule(target);
    },
    showGenerate() {
      if (!this.generateModal) {
        this.generateModal = new bootstrap.Modal(this.$refs.generateModal);
      }
      this.generateModal.show();
    },
    confirmGenerate() {
      this.generating = true;
      this.errorMessage = "";
      if (this.generateModal) {
        this.generateModal.hide();
      }

      const options = {
        mode: this.generateOptions.mode,
        policy: this.generateOptions.policy,
        shift_start: this.timeToMinutes(this.generateOptions.shift_start_time),
        shift_length: this.generateOptions.shift_length_hours * 60,
        break_length: this.generateOptions.break_length_hours * 60,
        inner_break_duration: this.generateOptions.inner_break_duration,
        inner_break_start: this.timeToMinutes(
          this.generateOptions.inner_break_start_time
        ),
        inner_break_end: this.timeToMinutes(
          this.generateOptions.inner_break_end_time
        ),
        day_lengths: this.generateOptions.day_lengths_hours.map((day) =>
          Math.round(day.value * 60)
        ),
        split_threshold: this.generateOptions.split_threshold,
        minimum_task_length: this.generateOptions.minimum_task_length,
        max_task_splits: this.generateOptions.max_task_splits,
        group_variants_threshold: this.generateOptions.group_variants_threshold,
        warehouse: this.warehouse,
      };

      http.fetch("/production/plan/generate", options).catch(() => {
        this.generating = false;
        this.errorMessage = this.$t("manufacturingGrant.scheduleError");
      });
    },
    timeToMinutes(timeStr) {
      const [h, m] = timeStr.split(":").map(Number);
      return h * 60 + m;
    },
    itemOpStatusClass(status) {
      if (status === undefined || status === null) return "bg-secondary";
      if (status <= -1) return "bg-warning text-dark";
      if (status === 0) return "bg-primary";
      if (status >= 3) return "bg-secondary";
      return "bg-info text-dark";
    },
  },
};
</script>
<style>
.stats-bar {
  border: 1px solid #e9ecef;
}
.stats-item {
  white-space: nowrap;
}
.stats-label {
  font-size: 0.85em;
  color: var(--gray);
  margin-right: 4px;
}
.stats-value {
  font-weight: 600;
  font-size: 0.95em;
}
.step-item {
  background: var(--bg);
  border: 1px solid transparent;
  transition: all 0.15s;
}
.step-item:hover {
  border-color: var(--default);
}
.step-active {
  background: var(--default);
  border: 1px solid var(--default);
}
.step-active .btn-light {
  border-color: rgba(255, 255, 255, 0.3);
  color: var(--default);
}
.step-active .btn-light:hover {
  background: rgba(255, 255, 255, 0.9);
}
.text-white-50 {
  color: rgba(255, 255, 255, 0.6);
}
.cursor-pointer {
  cursor: pointer;
}

/* ═══ NAPI NÉZET (design: Gyártási terv – napi nézet) ═══ */
.pp-header {
  display: flex;
  align-items: center;
  gap: 12px;
  flex-wrap: wrap;
  min-width: 0;
  margin-bottom: 10px;
}
.pp-daynav {
  display: flex;
  align-items: center;
  gap: 8px;
}
.pp-daytitle {
  min-width: 236px;
  padding: 0 4px;
}
.pp-daytitle .bold-24 {
  line-height: 1.75rem;
}
.pp-daytitle .regular-12 {
  margin-top: 2px;
}
.pp-actions {
  display: flex;
  align-items: center;
  gap: 12px;
  flex-wrap: wrap;
}
.pp-today {
  height: 40px;
  padding: 0 16px !important;
  font-weight: 600;
  font-size: 0.75rem !important;
  letter-spacing: 0.3px;
}
.pp-generate {
  display: flex;
  align-items: center;
  gap: 7px;
  white-space: nowrap;
}
.pp-generate svg {
  width: 15px;
  height: 15px;
}
.day-nav {
  width: 40px;
  height: 40px;
  border-radius: 8px;
  border: 1px solid var(--divider);
  background: var(--white);
  color: var(--gray);
  display: grid;
  place-items: center;
  cursor: pointer;
  transition: 0.2s;
}
.day-nav:hover {
  border-color: #a947d6;
  color: #a947d6;
}
.day-nav svg {
  width: 17px;
  height: 12px;
}
.pp-stats {
  display: flex;
  gap: 8px 20px;
  flex-wrap: wrap;
  padding: 8px 16px;
  margin-bottom: 10px;
}

/* hétsáv */
.week-strip {
  display: flex;
  gap: 6px;
  margin-bottom: 10px;
}
.week-day {
  flex: 1 1 0;
  min-width: 0;
  text-align: left;
  padding: 6px 10px;
  border-radius: 8px;
  border: 1px solid var(--divider-graph);
  background: var(--white-color);
  color: var(--black);
  cursor: pointer;
  font-family: inherit;
  transition: 0.2s;
}
.week-day:hover {
  border-color: #a947d6;
}
.week-day.today {
  background: var(--white);
  border-color: #a947d6;
}
.week-day.holiday {
  background: repeating-linear-gradient(45deg, #fff 0 6px, var(--bg) 6px 12px);
  color: var(--gray);
}
.week-day.selected {
  background: var(--main-grad);
  border-color: #713ae8;
  color: #fff;
}
.week-day .wd-top {
  display: flex;
  align-items: baseline;
  gap: 6px;
}
.week-day .wd-day {
  font-weight: 700;
  font-size: 1rem;
  line-height: 1.2;
}
.week-day .wd-dow {
  font-size: 0.625rem;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  opacity: 0.75;
}
.week-day .wd-ops {
  margin-left: auto;
  font-size: 0.625rem;
  font-weight: 600;
  opacity: 0.85;
  white-space: nowrap;
}
.week-day .wd-track {
  height: 4px;
  border-radius: 2px;
  background: var(--divider-graph);
  margin-top: 7px;
  overflow: hidden;
}
.week-day.selected .wd-track {
  background: rgba(255, 255, 255, 0.35);
}
.week-day .wd-fill {
  height: 100%;
  border-radius: 2px;
  background: var(--green);
}
.week-day.selected .wd-fill {
  background: #fff;
}

/* idősáv — napi */
.day-view {
  display: flex;
  flex-direction: column;
  min-height: 0;
}
.day-head {
  display: flex;
  border-bottom: 1px solid var(--divider);
  padding-bottom: 6px;
}
.day-head-title {
  width: 300px;
  flex-shrink: 0;
  padding-left: 4px;
  color: var(--gray-light);
}
.day-ruler {
  flex: 1;
  min-width: 0;
  display: flex;
  position: relative;
}
.day-hour {
  flex: 1 1 0;
  min-width: 0;
  border-left: 1px solid var(--divider-graph);
  padding-left: 6px;
  font-size: 0.6875rem;
  font-weight: 600;
  color: var(--gray);
  letter-spacing: 0.3px;
}
.day-body {
  position: relative;
  max-height: calc(100vh - 330px);
  overflow-y: auto;
  overflow-x: hidden;
  padding-top: 4px;
}
.day-item {
  display: flex;
  align-items: stretch;
  border-radius: 8px;
  margin-bottom: 4px;
}
.day-item:nth-child(2n) {
  background: #f6f3ff;
}
.day-title {
  width: 300px;
  flex-shrink: 0;
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 8px 12px 8px 6px;
}
.day-title-sep {
  width: 1px;
  align-self: stretch;
  margin: 6px 0;
  background: var(--divider);
  flex-shrink: 0;
}
.tl-avatar {
  width: 44px;
  height: 44px;
  flex-shrink: 0;
  border-radius: 8px;
  display: grid;
  place-items: center;
  background: #f1eafb;
  border: 1px dashed var(--divider);
  color: #713ae8;
  font-weight: 700;
  font-size: 0.8125rem;
  letter-spacing: 0.5px;
}
.tl-avatar.avatar-machine {
  background: var(--lyellow);
  color: #8a6a00;
}
.tl-avatar.avatar-product {
  background: var(--light);
  color: var(--default);
}
.tl-load {
  height: 4px;
  border-radius: 2px;
  background: var(--divider-graph);
  overflow: hidden;
  flex: 1;
}
.tl-load > div {
  height: 100%;
  border-radius: 2px;
}
.tl-load-label {
  font-size: 0.625rem;
  font-weight: 700;
  white-space: nowrap;
}
.day-row {
  flex: 1;
  min-width: 0;
  position: relative;
  height: 68px;
}
.day-grid {
  position: absolute;
  inset: 0;
  display: flex;
}
.day-grid > div {
  flex: 1 1 0;
  min-width: 0;
  border-left: 1px solid var(--divider-graph);
}
.day-empty {
  position: absolute;
  left: 14px;
  top: 50%;
  transform: translateY(-50%);
  pointer-events: none;
}
.day-now {
  position: absolute;
  top: 0;
  bottom: 0;
  width: 2px;
  background: var(--red);
  pointer-events: none;
  z-index: 6;
}
.day-now::before {
  content: "";
  position: absolute;
  top: 0;
  left: -4px;
  width: 10px;
  height: 10px;
  border-radius: 50%;
  background: var(--red);
}

/* műveletcsík — napi nézet */
.day-view .timeline-line {
  position: absolute;
  top: 6px;
  height: 56px;
  min-width: 14px;
  border-radius: 8px;
  border: 1px solid rgba(0, 0, 0, 0.06);
  box-shadow: 0 0 2px 1px rgba(202, 202, 202, 0.45);
  padding: 5px 7px;
  overflow: hidden;
  cursor: pointer;
  display: flex;
  flex-direction: column;
  justify-content: center;
  gap: 3px;
  transition: all 0.3s ease;
  box-sizing: border-box;
}
.day-view .timeline-line:hover {
  box-shadow: 0 0 6px 2px rgba(113, 58, 232, 0.4);
  transform: translateY(-1px);
}
.day-view .timeline-line.highlighted {
  box-shadow: 0 0 8px 2px rgba(255, 235, 59, 0.8);
  z-index: 5;
}
.day-view .timeline-line.bar-xs {
  padding: 4px;
  align-items: center;
  justify-content: center;
}
.day-view .timeline-color {
  width: 9px;
  height: 9px;
  border-radius: 50%;
  background: var(--gray-dark);
  pointer-events: none;
}
.day-view .tl-bar {
  display: flex;
  flex-wrap: nowrap;
  align-items: center;
  gap: 6px;
  min-width: 0;
  max-height: none;
  line-height: 1;
}
.day-view .tl-icon {
  width: 20px;
  height: 20px;
  flex-shrink: 0;
  border-radius: 5px;
  background: #fff;
  border: 1px solid rgba(0, 0, 0, 0.08);
  display: none;
  place-items: center;
  color: var(--gray-dark);
}
.day-view .tl-icon svg {
  width: 11px;
  height: 11px;
}
.day-view .tl-name {
  font-weight: 700;
  font-size: 0.75rem;
  line-height: 1rem;
  color: var(--gray-dark);
  white-space: nowrap;
  text-overflow: ellipsis;
}
.day-view .tl-qty {
  display: none;
  flex-shrink: 0;
  font-size: 0.625rem;
  font-weight: 700;
  color: var(--default);
  background: #fff;
  border-radius: 3px;
  padding: 1px 5px;
}
.day-view .tl-line2 {
  display: none;
  align-items: center;
  gap: 5px;
  min-width: 0;
}
.day-view .tl-serial {
  display: inline;
  font-size: 0.625rem;
  font-weight: 600;
  color: var(--gray);
  white-space: nowrap;
}
.day-view .tl-chars {
  display: none;
  gap: 3px;
  flex-shrink: 0;
}
.day-view .tl-char {
  font-size: 0.5625rem;
  padding: 1px 5px;
  border-radius: 3px;
  background: rgba(255, 255, 255, 0.75);
  color: var(--gray-dark);
  white-space: nowrap;
}
.day-view .tl-end {
  display: none;
  margin-left: auto;
  flex-shrink: 0;
  font-size: 0.625rem;
  color: var(--gray);
  white-space: nowrap;
}
.day-view .tl-progress {
  display: none;
  align-items: center;
  gap: 6px;
  min-width: 0;
}
.day-view .tl-track {
  flex: 1;
  min-width: 12px;
  height: 4px;
  border-radius: 2px;
  background: rgba(255, 255, 255, 0.7);
  overflow: hidden;
}
.day-view .tl-fill {
  height: 100%;
  border-radius: 2px;
}
.day-view .tl-pct {
  flex-shrink: 0;
  font-size: 0.5625rem;
  font-weight: 700;
  color: var(--gray);
  white-space: nowrap;
}
/* szélesség-adaptív láthatóság */
.day-view .timeline-line.bar-sm .tl-progress {
  display: flex;
}
.day-view .timeline-line.bar-md .tl-progress,
.day-view .timeline-line.bar-md .tl-line2,
.day-view .timeline-line.bar-md .tl-qty {
  display: flex;
}
.day-view .timeline-line.bar-lg .tl-progress,
.day-view .timeline-line.bar-lg .tl-line2,
.day-view .timeline-line.bar-lg .tl-qty,
.day-view .timeline-line.bar-lg .tl-end {
  display: flex;
}
.day-view .timeline-line.bar-lg .tl-icon {
  display: grid;
}
.day-view .timeline-line.bar-xl .tl-progress,
.day-view .timeline-line.bar-xl .tl-line2,
.day-view .timeline-line.bar-xl .tl-qty,
.day-view .timeline-line.bar-xl .tl-end {
  display: flex;
}
.day-view .timeline-line.bar-xl .tl-icon {
  display: grid;
}
.day-view .timeline-line.bar-xl .tl-chars {
  display: inline-flex;
}

/* művelet-szűrő */
.pp-opfilter {
  display: flex;
  align-items: center;
  gap: 6px;
  white-space: nowrap;
}
.pp-opfilter svg {
  width: 14px;
  height: 14px;
}
.pp-opfilter-badge {
  font-size: 0.625rem;
  font-weight: 700;
  color: #713ae8;
  background: #f1eafb;
  border-radius: 3px;
  padding: 1px 5px;
}
.pp-opfilter-menu {
  max-height: 360px;
  overflow-y: auto;
  min-width: 220px;
  padding: 6px 0;
}
.pp-opfilter-menu .dropdown-item {
  cursor: pointer;
  font-size: 0.8125rem;
  padding: 5px 14px;
}
.pp-opfilter-menu .form-check-input {
  accent-color: #713ae8;
}

/* szabadság */
.tl-leave-badge {
  flex-shrink: 0;
  font-size: 0.5625rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.4px;
  padding: 2px 6px;
  border-radius: 3px;
  background: #ffe3e3;
  color: var(--red);
}
.day-leave {
  position: absolute;
  top: 14px;
  bottom: 14px;
  left: 0;
  right: 0;
  border-radius: 8px;
  background: repeating-linear-gradient(45deg, #fff3f3 0 8px, #ffe8e8 8px 16px);
  display: flex;
  align-items: center;
  padding-left: 14px;
  color: var(--red);
  pointer-events: none;
}

/* részletek modal — művelet-csoportok */
.op-section-label {
  letter-spacing: 0.5px;
  margin-bottom: 5px;
}
.op-group-head {
  display: flex;
  align-items: center;
  gap: 8px;
  margin: 8px 0 2px;
}
.op-group-line {
  height: 1px;
  flex: 1;
  background: var(--divider);
}
.op-ord {
  flex-shrink: 0;
  min-width: 30px;
  height: 20px;
  padding: 0 5px;
  border-radius: 5px;
  display: grid;
  place-items: center;
  font-size: 0.625rem;
  font-weight: 700;
  background: var(--disabled);
  color: var(--default);
}
.op-ord.is-done {
  background: var(--lgreen);
  color: #0e7a52;
}
.op-ord.is-current {
  background: rgba(255, 255, 255, 0.22);
  color: #fff;
}
.op-current-badge {
  flex-shrink: 0;
  font-size: 0.5625rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.4px;
  padding: 2px 6px;
  border-radius: 3px;
  background: rgba(255, 255, 255, 0.22);
  color: #fff;
}

/* részletek modal — ki végezheti (dolgozó / gép) */
.op-access-row {
  display: flex;
  align-items: center;
  gap: 9px;
  padding: 6px 8px;
  border-radius: 8px;
  background: var(--bg);
  border: 1px solid transparent;
  min-width: 0;
  opacity: 0.65;
}
.op-access-row.assigned {
  background: var(--default-04);
  border-color: var(--default);
  opacity: 1;
}
.op-access-av {
  width: 30px;
  height: 30px;
  flex-shrink: 0;
  border-radius: 50%;
  display: grid;
  place-items: center;
  background: var(--divider);
  color: var(--gray);
  font-weight: 700;
  font-size: 0.6875rem;
}
.op-access-row.assigned .op-access-av {
  background: var(--default);
  color: #fff;
}
.op-access-av.machine {
  border-radius: 8px;
  background: var(--lyellow);
  color: #8a6a00;
}
.op-access-av.machine svg {
  width: 15px;
  height: 15px;
}
.op-access-badge {
  margin-left: auto;
  flex-shrink: 0;
  font-size: 0.5625rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.4px;
  padding: 2px 6px;
  border-radius: 3px;
  background: var(--default);
  color: #fff;
}
.op-access-count {
  font-size: 0.625rem;
  font-weight: 700;
  color: #713ae8;
  background: #f1eafb;
  border-radius: 3px;
  padding: 1px 5px;
  text-transform: none;
  letter-spacing: 0;
}

/* részletek modal — lépés-kártyák */
.st-track {
  flex: 1;
  height: 4px;
  border-radius: 2px;
  background: var(--divider);
  overflow: hidden;
  min-width: 24px;
}
.step-active .st-track {
  background: rgba(255, 255, 255, 0.3);
}
.st-fill {
  height: 100%;
  border-radius: 2px;
  background: var(--green);
}
.step-active .st-fill {
  background: #fff;
}
.st-btn {
  font-weight: 700 !important;
  font-size: 0.75rem !important;
  padding: 4px 6px !important;
}
</style>
